<template>
  <div v-if="photoUrl" class="inline-block">
    <img :src="photoUrl" :class="sizeClass" class="rounded-full" :alt="userName" @error="onImageError" />
  </div>
  <div v-else :class="sizeClass" class="rounded-full bg-gray-600 text-white flex items-center justify-center font-semibold" :style="{ fontSize: fontSizeStyle }">
    {{ initials }}
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { getUserPhoto } from '@/js/userPhotoCache'

const props = defineProps({
  userEmail: {
    type: String,
    default: null
  },
  userName: {
    type: String,
    required: true
  },
  size: {
    type: String,
    default: 'small' // 'small', 'medium', 'large'
  }
})

// Data
const photoUrl = ref(null)
const isLoading = ref(false)

// Computed
const initials = computed(() => {
  if (!props.userName) return '?'

  const nameParts = props.userName.trim().split(' ')
  if (nameParts.length === 0) return '?'

  if (nameParts.length === 1) {
    // Single name, take first two letters
    return nameParts[0].substring(0, 2).toUpperCase()
  }

  // Take first letter of first and last name
  const firstInitial = nameParts[0].charAt(0).toUpperCase()
  const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase()
  return firstInitial + lastInitial
})

const sizeClass = computed(() => {
  const sizes = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12'
  }
  return sizes[props.size] || sizes.small
})

const fontSizeStyle = computed(() => {
  const fontSizes = {
    small: '6px',
    medium: '12px',
    large: '18px'
  }
  return fontSizes[props.size] || fontSizes.small
})

// Methods
const loadUserPhoto = async () => {
  if (!props.userEmail) {
    console.log('[UserAvatar] No email provided for:', props.userName)
    return
  }

  if (isLoading.value) {
    return // Prevent duplicate requests
  }

  isLoading.value = true
  console.log('[UserAvatar] Loading photo for:', props.userName, '(' + props.userEmail + ')')

  try {
    const photo = await getUserPhoto(props.userEmail)
    if (photo) {
      photoUrl.value = photo
      console.log('[UserAvatar] Photo loaded for:', props.userName)
    } else {
      console.log('[UserAvatar] No photo available for:', props.userName, '- showing initials')
    }
  } catch (error) {
    console.error('[UserAvatar] Error loading photo for:', props.userName, error)
  } finally {
    isLoading.value = false
  }
}

const onImageError = () => {
  console.error('[UserAvatar] Failed to display image for:', props.userName)
  photoUrl.value = null // Fall back to initials
}

// Lifecycle
onMounted(async () => {
  await loadUserPhoto()
})

// Watchers
watch(() => props.userEmail, async (newEmail) => {
  if (newEmail) {
    await loadUserPhoto()
  }
})
</script>
